/**
 * FieldPanel — GNSS capture, forms, offline queue → real map features
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  TextField,
  Alert,
  Stack,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { PhoneAndroid, GpsFixed, Sync, MyLocation } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  addFeatureAsync,
  addLayer,
  createLayerAsync,
  setActiveLayer,
  updateLayer,
} from '../../../store/slices/layersSlice';
import { addNotification } from '../../../store/slices/uiSlice';
import { mapNavigator } from '../../../services/MapNavigator';

const QUEUE_KEY = 'geovera_offline_queue';
const FORM_KEY = 'geovera_form_template';

interface FieldCapture {
  id: number;
  position: { lat: number; lon: number; accuracy?: number; altitude?: number | null };
  form: Record<string, string>;
  timestamp: string;
}

const FIELD_LAYER_NAME = 'Field captures';
const LOCAL_FIELD_LAYER_NAME = 'Field captures (local)';
const FIELD_LAYER_SYMBOLOGY = {
  type: 'simple' as const,
  fillColor: '#26a69a',
  strokeColor: '#ffffff',
  strokeWidth: 2,
  size: 8,
  opacity: 1,
};

const isLocalFieldLayerId = (id: string) => id.startsWith('field-local-') || /^field-\d+$/.test(id);

const FieldPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const layers = useAppSelector((s) => s.layers.layers);
  const [gpsEnabled, setGpsEnabled] = useState(false);
  const [currentPosition, setCurrentPosition] = useState<{
    lat: number;
    lon: number;
    accuracy?: number;
    altitude?: number | null;
  } | null>(null);
  const [formValues, setFormValues] = useState({ description: '', code: '' });
  const [formSchema, setFormSchema] = useState<
    Array<{ name: string; type: string; label: string }>
  >([
    { name: 'code', type: 'string', label: 'Feature Code' },
    { name: 'description', type: 'string', label: 'Description' },
  ]);
  const [offlineQueue, setOfflineQueue] = useState<FieldCapture[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const watchId = useRef<number | null>(null);

  useEffect(() => {
    try {
      const q = localStorage.getItem(QUEUE_KEY);
      if (q) setOfflineQueue(JSON.parse(q));
      const t = localStorage.getItem(FORM_KEY);
      if (t) setFormSchema(JSON.parse(t));
    } catch {
      /* ignore */
    }
    return () => {
      if (watchId.current != null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchId.current);
      }
    };
  }, []);

  const persistQueue = (q: FieldCapture[]) => {
    setOfflineQueue(q);
    try {
      localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
    } catch {
      /* ignore */
    }
  };

  const startGNSS = () => {
    if (!('geolocation' in navigator)) {
      dispatch(
        addNotification({
          type: 'error',
          message: 'This browser does not support geolocation',
        })
      );
      return;
    }
    setGpsEnabled(true);
    watchId.current = navigator.geolocation.watchPosition(
      (pos) => {
        setCurrentPosition({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          altitude: pos.coords.altitude,
        });
      },
      (err) => {
        dispatch(
          addNotification({
            type: 'warning',
            message: `GNSS: ${err.message}`,
          })
        );
      },
      { enableHighAccuracy: true, maximumAge: 2000, timeout: 15000 }
    );
  };

  const stopGNSS = () => {
    setGpsEnabled(false);
    if (watchId.current != null) {
      navigator.geolocation.clearWatch(watchId.current);
      watchId.current = null;
    }
  };

  const ensureLocalFieldLayer = useCallback(() => {
    const existing = layers.find((l) => l.geometryType === 'Point' && isLocalFieldLayerId(l.id));
    if (existing) {
      if (existing.name !== LOCAL_FIELD_LAYER_NAME) {
        dispatch(updateLayer({ id: existing.id, updates: { name: LOCAL_FIELD_LAYER_NAME } }));
      }
      return existing;
    }
    const id = `field-local-${Date.now()}`;
    const layer = {
      id,
      name: LOCAL_FIELD_LAYER_NAME,
      type: 'vector' as const,
      geometryType: 'Point' as const,
      visible: true,
      opacity: 1,
      zIndex: layers.length + 1,
      featureCount: 0,
      features: [] as any[],
      symbology: FIELD_LAYER_SYMBOLOGY,
    };
    dispatch(addLayer(layer));
    dispatch(setActiveLayer(id));
    return layer;
  }, [dispatch, layers]);

  const captureProperties = (capture: FieldCapture, recordState: 'local' | 'remote') => ({
    ...capture.form,
    accuracy_m: capture.position.accuracy,
    altitude: capture.position.altitude,
    captured_at: capture.timestamp,
    client_capture_id: String(capture.id),
    record_state: recordState,
    source: recordState === 'local' ? 'field-gnss-local' : 'field-gnss',
  });

  const writeCaptureToMap = (capture: FieldCapture) => {
    const layerMeta = ensureLocalFieldLayer();
    const layerId = layerMeta.id;
    const existing = layers.find((l) => l.id === layerId);
    const feature = {
      id: `field-f-${capture.id}`,
      layerId,
      geometry: {
        type: 'Point' as const,
        coordinates: [capture.position.lon, capture.position.lat],
      },
      properties: captureProperties(capture, 'local'),
    };
    const prev = existing?.features || [];
    if (prev.some((f) => f.id === feature.id)) return layerId;
    dispatch(
      updateLayer({
        id: layerId,
        updates: {
          features: [...prev, feature],
          featureCount: prev.length + 1,
        },
      })
    );
    return layerId;
  };

  const removeLocalPreviews = (captureIds: Set<number>, fallbackLayerId?: string) => {
    if (captureIds.size === 0) return;
    const localLayer = layers.find((l) => l.geometryType === 'Point' && isLocalFieldLayerId(l.id));
    const layerId = localLayer?.id || fallbackLayerId;
    if (!layerId) return;
    const features = (localLayer?.features || []).filter(
      (feature) => !captureIds.has(Number(String(feature.id).replace('field-f-', '')))
    );
    dispatch(
      updateLayer({
        id: layerId,
        updates: { features, featureCount: features.length },
      })
    );
  };

  const persistCaptures = async (captures: FieldCapture[]) => {
    const remoteLayer = layers.find(
      (l) => l.name === FIELD_LAYER_NAME && l.geometryType === 'Point' && !isLocalFieldLayerId(l.id)
    );
    let remoteLayerId = remoteLayer?.id;
    const persistedCaptureIds = new Set(
      (remoteLayer?.features || [])
        .map((feature) => feature.properties?.client_capture_id)
        .filter((id): id is string => typeof id === 'string')
    );

    if (!remoteLayerId) {
      const createResult = await dispatch(
        createLayerAsync({
          name: FIELD_LAYER_NAME,
          geometryType: 'Point',
          crs: 'EPSG:4326',
          symbology: FIELD_LAYER_SYMBOLOGY,
          visible: true,
          zOrder: layers.length + 1,
        })
      );
      if (!createLayerAsync.fulfilled.match(createResult)) {
        throw new Error(
          typeof createResult.payload === 'string'
            ? createResult.payload
            : 'Could not create the remote field layer'
        );
      }
      remoteLayerId = createResult.payload.id;
    }
    if (!remoteLayerId) throw new Error('Remote field layer did not return an id');

    const successfulIds = new Set<number>();
    for (const capture of captures) {
      if (persistedCaptureIds.has(String(capture.id))) {
        successfulIds.add(capture.id);
        continue;
      }
      const result = await dispatch(
        addFeatureAsync({
          layerId: remoteLayerId,
          geometry: {
            type: 'Point',
            coordinates: [capture.position.lon, capture.position.lat],
          },
          properties: captureProperties(capture, 'remote'),
        })
      );
      if (addFeatureAsync.fulfilled.match(result)) {
        successfulIds.add(capture.id);
        persistedCaptureIds.add(String(capture.id));
      }
    }
    return successfulIds;
  };

  const capturePoint = async () => {
    if (!currentPosition) {
      dispatch(addNotification({ type: 'warning', message: 'Wait for a GNSS fix first' }));
      return;
    }
    const capture: FieldCapture = {
      id: Date.now(),
      position: currentPosition,
      form: { ...formValues },
      timestamp: new Date().toISOString(),
    };
    const localLayerId = writeCaptureToMap(capture);
    mapNavigator.zoomToLonLat(currentPosition.lon, currentPosition.lat, 18);
    const queued = [...offlineQueue, capture];
    persistQueue(queued);
    setIsSyncing(true);
    try {
      const successfulIds = await persistCaptures([capture]);
      if (successfulIds.has(capture.id)) {
        persistQueue(queued.filter((item) => !successfulIds.has(item.id)));
        removeLocalPreviews(successfulIds, localLayerId);
        dispatch(
          addNotification({
            type: 'success',
            message: `Captured and synced ${formValues.code || 'point'}`,
          })
        );
      } else {
        dispatch(
          addNotification({
            type: 'warning',
            message: 'Capture saved locally; remote sync failed and remains queued',
          })
        );
      }
    } catch (error) {
      dispatch(
        addNotification({
          type: 'warning',
          message: `Capture remains queued: ${error instanceof Error ? error.message : 'sync failed'}`,
        })
      );
    } finally {
      setIsSyncing(false);
    }
  };

  const captureWithQueue = () => {
    if (!currentPosition) {
      dispatch(addNotification({ type: 'warning', message: 'Enable GNSS first' }));
      return;
    }
    const capture: FieldCapture = {
      id: Date.now(),
      position: currentPosition,
      form: { ...formValues },
      timestamp: new Date().toISOString(),
    };
    writeCaptureToMap(capture);
    mapNavigator.zoomToLonLat(currentPosition.lon, currentPosition.lat, 18);
    persistQueue([...offlineQueue, capture]);
    dispatch(
      addNotification({
        type: 'success',
        message: 'Saved locally and added to the offline queue',
      })
    );
  };

  const syncOffline = async () => {
    if (offlineQueue.length === 0) return;
    const queued = [...offlineQueue];
    setIsSyncing(true);
    try {
      const successfulIds = await persistCaptures(queued);
      const remaining = queued.filter((capture) => !successfulIds.has(capture.id));
      persistQueue(remaining);
      removeLocalPreviews(successfulIds);

      if (successfulIds.size === queued.length) {
        dispatch(
          addNotification({
            type: 'success',
            message: `Synced ${successfulIds.size} field record(s)`,
          })
        );
      } else if (successfulIds.size > 0) {
        dispatch(
          addNotification({
            type: 'warning',
            message: `${successfulIds.size} field record(s) synced; ${remaining.length} remain queued`,
          })
        );
      } else {
        dispatch(
          addNotification({
            type: 'error',
            message: `Sync failed; all ${remaining.length} field record(s) remain queued`,
          })
        );
      }
    } catch (error) {
      dispatch(
        addNotification({
          type: 'error',
          message: `Sync failed; all ${queued.length} field record(s) remain queued: ${error instanceof Error ? error.message : 'remote service unavailable'}`,
        })
      );
    } finally {
      setIsSyncing(false);
    }
  };

  const goToFix = () => {
    if (!currentPosition) return;
    mapNavigator.zoomToLonLat(currentPosition.lon, currentPosition.lat, 17);
  };

  const addFormField = () => {
    setFormSchema([
      ...formSchema,
      { name: `field_${Date.now()}`, type: 'string', label: 'New Field' },
    ]);
  };

  const saveAsTemplate = () => {
    try {
      localStorage.setItem(FORM_KEY, JSON.stringify(formSchema));
      dispatch(addNotification({ type: 'success', message: 'Form template saved on device' }));
    } catch {
      dispatch(addNotification({ type: 'error', message: 'Could not save template' }));
    }
  };

  return (
    <Box sx={{ p: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <PhoneAndroid color="info" />
        <Typography variant="subtitle2" fontWeight={700}>
          FIELD & GNSS
        </Typography>
      </Box>

      <FormControlLabel
        control={
          <Switch
            checked={gpsEnabled}
            onChange={(e) => {
              if (e.target.checked) startGNSS();
              else stopGNSS();
            }}
          />
        }
        label="High-accuracy GNSS"
      />

      {currentPosition && (
        <Alert
          severity={
            currentPosition.accuracy != null && currentPosition.accuracy < 15 ? 'success' : 'info'
          }
          sx={{ mt: 1, fontSize: 12 }}
          action={
            <Button
              size="small"
              startIcon={<MyLocation />}
              onClick={goToFix}
              sx={{ textTransform: 'none' }}
            >
              Go to
            </Button>
          }
        >
          {currentPosition.lat.toFixed(6)}, {currentPosition.lon.toFixed(6)}
          <br />±{currentPosition.accuracy?.toFixed(1) ?? '—'} m
          {currentPosition.altitude != null
            ? ` · alt ${currentPosition.altitude.toFixed(1)} m`
            : ''}
        </Alert>
      )}

      <Divider sx={{ my: 1.5 }} />

      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
        Capture form
      </Typography>
      <TextField
        size="small"
        fullWidth
        label="Feature code"
        value={formValues.code}
        onChange={(e) => setFormValues({ ...formValues, code: e.target.value })}
        sx={{ mb: 1 }}
      />
      <TextField
        size="small"
        fullWidth
        multiline
        rows={2}
        label="Description / notes"
        value={formValues.description}
        onChange={(e) => setFormValues({ ...formValues, description: e.target.value })}
      />

      <Stack direction="row" spacing={1} sx={{ mt: 1.25 }} flexWrap="wrap" useFlexGap>
        <Button
          variant="contained"
          size="small"
          startIcon={<GpsFixed />}
          onClick={capturePoint}
          disabled={!currentPosition || isSyncing}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Capture & sync
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={captureWithQueue}
          disabled={!currentPosition || isSyncing}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Capture + offline queue
        </Button>
      </Stack>

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" fontWeight={600}>
          Form fields
        </Typography>
        <Button size="small" onClick={addFormField} sx={{ ml: 1, textTransform: 'none' }}>
          + Field
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={saveAsTemplate}
          sx={{ ml: 0.5, textTransform: 'none' }}
        >
          Save template
        </Button>
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
          {formSchema.map((f) => f.label).join(' · ')}
        </Typography>
      </Box>

      <Button
        fullWidth
        variant="outlined"
        size="small"
        sx={{ mt: 1.5, textTransform: 'none', fontWeight: 600 }}
        startIcon={<Sync />}
        onClick={syncOffline}
        disabled={offlineQueue.length === 0 || isSyncing}
      >
        {isSyncing ? 'Syncing field records…' : `Sync offline queue (${offlineQueue.length})`}
      </Button>

      {offlineQueue.length > 0 && (
        <List dense sx={{ mt: 0.5, maxHeight: 120, overflow: 'auto' }}>
          {offlineQueue.slice(-5).map((c) => (
            <ListItem key={c.id} dense>
              <ListItemText
                primary={c.form.code || 'Capture'}
                secondary={`${c.position.lat.toFixed(5)}, ${c.position.lon.toFixed(5)} · ${new Date(c.timestamp).toLocaleTimeString()}`}
                primaryTypographyProps={{ fontSize: 12 }}
                secondaryTypographyProps={{ fontSize: 11 }}
              />
            </ListItem>
          ))}
        </List>
      )}

      <Alert severity="info" sx={{ mt: 1.5, fontSize: 11 }}>
        Captures are previewed in “Field captures (local)”. Successful sync replaces local previews
        with remote “Field captures” records; failed records stay queued across reloads.
      </Alert>
    </Box>
  );
};

export default FieldPanel;
